import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HiShieldExclamation } from 'react-icons/hi';

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
      <p className="mt-4 text-slate-600">Loading...</p>
    </div>
  </div>
);

/**
 * SECURITY: Unauthorized access component for non-admin users
 * Shows a clear message when a regular user tries to access admin routes
 */
const UnauthorizedAccess = ({ onLogout }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
        <HiShieldExclamation className="w-10 h-10 text-red-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
      <p className="text-slate-600 mb-6">
        You don't have permission to access the admin dashboard. 
        This area is restricted to administrators only.
      </p>
      <button
        onClick={onLogout}
        className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
      >
        Return to Login
      </button>
    </div>
  </div>
);

/**
 * SECURITY: Protected route component that requires admin role
 * - Redirects unauthenticated users to login
 * - Shows unauthorized message for non-admin users
 * - Only renders children for authenticated admin users
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  // SECURITY: Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // SECURITY: Check if user has admin role
  // This prevents non-admin users from accessing the admin dashboard UI
  if (user.role !== 'admin') {
    return <UnauthorizedAccess onLogout={logout} />;
  }

  return children;
};

export default ProtectedRoute;

